/**
 * CSV Export Service using PapaParse
 * A common service for exporting data to CSV format with dynamic data handling
 */

import Papa from 'papaparse';

export interface CSVExportOptions {
  data: any[];
  filename?: string;
  columns?: ColumnConfig[];
  dateRange?: string;
  excludeColumns?: string[];
  includeTimestamp?: boolean;
  customHeaders?: Record<string, string>;
  includeMetadata?: boolean;
}

export interface ColumnConfig {
  key: string;
  header: string;
  formatter?: (value: any, row?: any) => string | number;
}

/**
 * Generates filename with timestamp
 */
const generateFilename = (baseFilename: string, includeTimestamp: boolean): string => {
  const name = baseFilename.replace(/\.csv$/i, '');
  
  if (includeTimestamp) {
    const timestamp = new Date()
      .toISOString()
      .replace(/[:.]/g, '-')
      .slice(0, 19);
    return `${name}_${timestamp}.csv`;
  }
  
  return `${name}.csv`;
};

/**
 * Formats header names from camelCase or snake_case to Title Case
 */
const formatHeaderName = (key: string): string => {
  return key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Extracts column configuration from data
 */
const extractColumns = (
  data: any[],
  options: CSVExportOptions
): ColumnConfig[] => {
  const { columns, excludeColumns = [], customHeaders = {} } = options;

  // If columns are explicitly provided, use them
  if (columns && columns.length > 0) {
    return columns;
  }

  // Auto-detect columns from the first data row
  if (data.length === 0) {
    return [];
  }

  const firstRow = data[0];
  const keys = Object.keys(firstRow).filter(
    (key) => !excludeColumns.includes(key)
  );

  return keys.map((key) => ({
    key,
    header: customHeaders[key] || formatHeaderName(key),
  }));
};

/**
 * Transforms data based on column configuration
 */
const transformData = (data: any[], columns: ColumnConfig[]): any[] => {
  return data.map((row) => {
    const transformedRow: any = {};
    
    columns.forEach((col) => {
      const value = row[col.key];
      
      // Use custom formatter if provided
      if (col.formatter) {
        transformedRow[col.header] = col.formatter(value, row);
      } else {
        transformedRow[col.header] = value;
      }
    });
    
    return transformedRow;
  });
};

/**
 * Triggers browser download of CSV file
 */
const triggerDownload = (content: string, filename: string): void => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if ((navigator as any).msSaveBlob) {
    // IE 10+
    (navigator as any).msSaveBlob(blob, filename);
  } else {
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up the URL
    setTimeout(() => URL.revokeObjectURL(url), 100);
  }
};

/**
 * Main export function - exports data as CSV using PapaParse
 * 
 * @param options - Configuration options for CSV export
 * @returns Promise that resolves when download is triggered
 * 
 * @example
 * // Basic usage
 * exportToCSV({
 *   data: userData,
 *   filename: 'users.csv'
 * });
 * 
 * @example
 * // With custom columns and formatters
 * exportToCSV({
 *   data: callData,
 *   filename: 'call-analytics.csv',
 *   columns: [
 *     { key: 'call_id', header: 'Call ID' },
 *     { key: 'duration_minutes', header: 'Duration (min)' },
 *     { 
 *       key: 'positive_score', 
 *       header: 'Sentiment',
 *       formatter: (value) => `${value}%`
 *     }
 *   ],
 *   dateRange: '2024-01-01 to 2024-01-31',
 *   includeTimestamp: true
 * });
 */
export const exportToCSV = (options: CSVExportOptions): void => {
  const {
    data,
    filename = 'export.csv',
    includeTimestamp = false,
    dateRange,
    includeMetadata = true,
  } = options;

  // Validate data
  if (!data || !Array.isArray(data) || data.length === 0) {
    console.warn('No data provided for CSV export');
    return;
  }

  try {
    // Extract columns
    const columns = extractColumns(data, options);

    if (columns.length === 0) {
      console.warn('No columns detected for CSV export');
      return;
    }

    // Transform data with formatters
    const transformedData = transformData(data, columns);

    // Generate CSV using PapaParse
    let csvContent = Papa.unparse(transformedData, {
      quotes: true, // Wrap all fields in quotes for safety
      delimiter: ',',
      header: true,
      newline: '\n',
    });

    // Add metadata if requested
    if (includeMetadata && dateRange) {
      const metadataLines = [
        `Date Range:,${dateRange}`,
        '', // Empty line
      ];
      csvContent = metadataLines.join('\n') + '\n' + csvContent;
    }

    // Generate filename
    const finalFilename = generateFilename(filename, includeTimestamp);

    // Trigger download
    triggerDownload(csvContent, finalFilename);

    console.log(`CSV export successful: ${finalFilename}`);
  } catch (error) {
    console.error('Error exporting to CSV:', error);
    throw new Error(`Failed to export CSV: ${error}`);
  }
};

/**
 * Exports multiple datasets as separate sheets (combined in single CSV with separators)
 * 
 * @param datasets - Array of dataset configurations
 * @param filename - Output filename
 * @param includeTimestamp - Whether to add timestamp to filename
 * 
 * @example
 * exportMultipleSheetsToCSV([
 *   { title: 'User Analytics', data: userData, columns: userColumns },
 *   { title: 'Call Analytics', data: callData, columns: callColumns }
 * ], 'combined-analytics.csv');
 */
export const exportMultipleSheetsToCSV = (
  datasets: Array<{
    title: string;
    data: any[];
    columns?: ColumnConfig[];
    customHeaders?: Record<string, string>;
  }>,
  filename: string = 'export.csv',
  includeTimestamp: boolean = false
): void => {
  if (!datasets || datasets.length === 0) {
    console.warn('No datasets provided for CSV export');
    return;
  }

  try {
    const csvParts: string[] = [];

    datasets.forEach((dataset, index) => {
      const { title, data, columns, customHeaders } = dataset;

      // Add sheet title
      csvParts.push(`\n${title}\n`);
      csvParts.push('='.repeat(50) + '\n');

      // Extract columns for this dataset
      const datasetColumns = extractColumns(data, { 
        data, 
        columns, 
        customHeaders 
      });

      // Transform data
      const transformedData = transformData(data, datasetColumns);

      // Generate CSV for this dataset using PapaParse
      const datasetCSV = Papa.unparse(transformedData, {
        quotes: true,
        delimiter: ',',
        header: true,
        newline: '\n',
      });

      csvParts.push(datasetCSV);

      // Add separator between datasets (except after last one)
      if (index < datasets.length - 1) {
        csvParts.push('\n\n');
      }
    });

    const combinedContent = csvParts.join('');

    // Generate filename and trigger download
    const finalFilename = generateFilename(filename, includeTimestamp);
    triggerDownload(combinedContent, finalFilename);

    console.log(`Multi-sheet CSV export successful: ${finalFilename}`);
  } catch (error) {
    console.error('Error exporting multi-sheet CSV:', error);
    throw new Error(`Failed to export multi-sheet CSV: ${error}`);
  }
};

/**
 * Parse CSV file content to JSON
 * Useful for importing CSV files
 * 
 * @param file - File object to parse
 * @returns Promise with parsed data
 * 
 * @example
 * const file = event.target.files[0];
 * const data = await parseCSVFile(file);
 * console.log(data);
 */
export const parseCSVFile = (file: File): Promise<any[]> => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length > 0) {
          console.warn('CSV parsing warnings:', results.errors);
        }
        resolve(results.data);
      },
      error: (error) => {
        reject(error);
      },
    });
  });
};

/**
 * Parse CSV string to JSON
 * 
 * @param csvString - CSV content as string
 * @returns Parsed data array
 * 
 * @example
 * const data = parseCSVString(csvContent);
 */
export const parseCSVString = (csvString: string): any[] => {
  const results = Papa.parse(csvString, {
    header: true,
    dynamicTyping: true,
    skipEmptyLines: true,
  });

  if (results.errors.length > 0) {
    console.warn('CSV parsing warnings:', results.errors);
  }

  return results.data;
};

// ==========================================
// Utility Formatters
// ==========================================

/**
 * Utility function to format date values for CSV
 */
export const formatDateForCSV = (date: Date | string | number): string => {
  if (!date) return '';
  
  const d = new Date(date);
  if (isNaN(d.getTime())) return String(date);
  
  return d.toISOString().split('T')[0]; // YYYY-MM-DD
};

/**
 * Utility function to format date-time values for CSV
 */
export const formatDateTimeForCSV = (date: Date | string | number): string => {
  if (!date) return '';
  
  const d = new Date(date);
  if (isNaN(d.getTime())) return String(date);
  
  return d.toISOString().replace('T', ' ').slice(0, 19); // YYYY-MM-DD HH:mm:ss
};

/**
 * Utility function to format numbers with specific decimal places
 */
export const formatNumberForCSV = (value: number, decimals: number = 2): string | number => {
  if (value === null || value === undefined || isNaN(value)) return '';
  return parseFloat(value.toFixed(decimals));
};

/**
 * Utility function to format percentage values
 */
export const formatPercentageForCSV = (value: number, decimals: number = 2): string => {
  if (value === null || value === undefined || isNaN(value)) return '';
  return `${value.toFixed(decimals)}%`;
};

/**
 * Utility function to format currency values
 */
export const formatCurrencyForCSV = (
  value: number,
  currency: string = 'USD',
  decimals: number = 2
): string => {
  if (value === null || value === undefined || isNaN(value)) return '';
  return `${currency} ${value.toFixed(decimals)}`;
};

/**
 * Utility function to format boolean values
 */
export const formatBooleanForCSV = (value: boolean): string => {
  if (value === null || value === undefined) return '';
  return value ? 'Yes' : 'No';
};

/**
 * Utility function to format array values
 */
export const formatArrayForCSV = (value: any[], separator: string = ', '): string => {
  if (!Array.isArray(value)) return '';
  return value.join(separator);
};

export default exportToCSV;
